import { useSyncExternalStore } from 'react';
import { supabase } from '../supabaseClient';
import { currentAcademicYear, isAcademicYearLocked } from './classAcademicYear';

/** 전역 학년도 선택(계정 메뉴). localStorage + 이벤트로 공유, Provider 없음. */
const STORAGE_KEY = 'cp.selectedAcademicYear.v1';
const EVENT = 'cp-selected-academic-year-changed';

type Stored = { year: number; bundleId: string | null };

function readStored(): Stored | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    if (typeof v.year !== 'number' || !Number.isFinite(v.year)) return null;
    if (v.year > currentAcademicYear() + 1) return null;
    // 다음 학년도 선택은 서버 동기화(initSelectedAcademicYear)가 이용권을 확인해 바로잡는다
    return { year: v.year, bundleId: typeof v.bundleId === 'string' ? v.bundleId : null };
  } catch {
    return null;
  }
}

function writeStored(v: Stored) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(EVENT));
}

export function getSelectedAcademicYear(): number {
  return readStored()?.year ?? currentAcademicYear();
}

/** 선택한 학년도 묶음 id (모르면 null → 서버 기본값 사용) */
export function getSelectedBundleId(): string | null {
  return readStored()?.bundleId ?? null;
}

export function isSelectedYearReadOnly(): boolean {
  return isAcademicYearLocked(getSelectedAcademicYear());
}

/** 이용권이 덮는 현재·다음 학년도(서버 판정). 실패 시 빈 배열 */
export async function fetchMyPassYears(): Promise<number[]> {
  const { data, error } = await supabase.rpc('cp_my_pass_years');
  if (error || !Array.isArray(data)) return [];
  return (data as unknown[]).map(Number).filter((n) => Number.isFinite(n));
}

const AUTO_SELECTED_KEY = 'cp.autoSelectedPassYear.v1';

/**
 * 앱 시작 시 선택 동기화. 다음 학년도 이용권이 새로 생겼으면 그 학년도를 한 번만 자동 선택한다
 * (구매 직후 또는 구매 뒤 첫 접속). 이후에는 사용자가 고른 학년도를 유지.
 */
export async function initSelectedAcademicYear(): Promise<boolean> {
  const before = readStored()?.year ?? null;
  const next = currentAcademicYear() + 1;
  const passYears = await fetchMyPassYears();
  let autoDone = false;
  try {
    autoDone = window.localStorage.getItem(AUTO_SELECTED_KEY) === String(next);
  } catch {
    /* ignore */
  }
  if (passYears.includes(next) && !autoDone) {
    const res = await syncSelectedAcademicYear(next);
    if (res) {
      try {
        window.localStorage.setItem(AUTO_SELECTED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return before !== next;
    }
  }
  let hasStored = false;
  try {
    hasStored = !!window.localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  const stored = hasStored ? getSelectedAcademicYear() : null;
  // 이용권 없는 다음 학년도가 저장돼 있으면(환불 등) 기본 학년도로
  const want = stored != null && stored > currentAcademicYear() && !passYears.includes(stored) ? null : stored;
  const res = await syncSelectedAcademicYear(want);
  const finalRes = res ?? (want != null ? await syncSelectedAcademicYear(null) : null);
  return before != null && finalRes != null && finalRes.year !== before;
}

type SelectResult = { academic_year: number; bundle_id: string | null };

/** 서버에 선택을 저장하고 묶음을 확보(현재·다음 학년도). year 미지정 = 기본 학년도 */
export async function syncSelectedAcademicYear(year?: number | null): Promise<Stored | null> {
  const { data, error } = await supabase.rpc('cp_select_academic_year', {
    p_year: year ?? null,
  });
  if (error || !data) return null;
  const r = data as SelectResult;
  const next: Stored = { year: Number(r.academic_year), bundleId: r.bundle_id ?? null };
  const prev = readStored();
  if (!prev || prev.year !== next.year || prev.bundleId !== next.bundleId) writeStored(next);
  return next;
}

/** 학년도 전환: 저장 후 전체 새로고침(페이지별 캐시를 확실히 비움) */
export async function switchAcademicYear(year: number): Promise<boolean> {
  const res = await syncSelectedAcademicYear(year);
  if (!res) return false;
  window.location.reload();
  return true;
}

function subscribe(cb: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function useSelectedAcademicYear(): number {
  return useSyncExternalStore(subscribe, getSelectedAcademicYear, currentAcademicYear);
}

/** 목록 조회 조건에 선택한 학년도 묶음을 더한다(묶음 id 모르면 그대로) */
export function withSelectedBundle<T extends Record<string, unknown>>(cond: T): T & { bundle_id?: string } {
  const bundleId = getSelectedBundleId();
  return bundleId ? { ...cond, bundle_id: bundleId } : cond;
}
